import { FormControlLabel, MenuItem, Radio, Select, Tooltip } from '@material-ui/core'
import { Button } from 'components/facets'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import ImagePopover from 'components/facets/ImagePopover'
import { getGameCodeFromName } from 'lib/utils'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table } from 'react-virtualized'
import {
  generateArticleKey,
  getGameFromId,
  getPricesMapForeachInventoryArticle,
  InventoryArticle,
  InventoryPricesMap,
} from 'shared'
import { getProductImageUrl } from 'utils/imageUtils'

export interface InventoryPriceTableProps {
  fetchedStock: InventoryArticle[]
  // The table only says which prices the user settled on; the flow is the
  // caller's business.
  onConfirm: (updatedInventory: InventoryArticle[]) => void
  setUpdatedStockAfterSettingsPricesAndComments?: React.Dispatch<
    React.SetStateAction<InventoryArticle[]>
  >
  confirmLabel?: string
}

export const InventoryPriceTable = ({
  fetchedStock,
  onConfirm,
  setUpdatedStockAfterSettingsPricesAndComments,
  confirmLabel,
}: InventoryPriceTableProps): JSX.Element => {
  const { t } = useTranslation()
  const pricesMapForeachInventoryArticle = getPricesMapForeachInventoryArticle(fetchedStock)

  // Use a backing data structure to update prices based on user choice
  const [workingFetchedStockMap, setWorkingFetchedStockMap] = useState<InventoryPricesMap>(
    pricesMapForeachInventoryArticle
  )

  const getMin = (prices: number[]): number => Math.min(...prices)
  const getAverage = (prices: number[]): number =>
    Number((prices.reduce((acc, price) => acc + price, 0) / prices.length).toFixed(2))
  const getMax = (prices: number[]): number => Math.max(...prices)

  /**
   *
   * updates a single row that the user interacts with. This function is called both when the user clicks the radio button
   * and when the user uses the select.
   * Based on the row chosen by the user, it generates the key to search for the item within the WorkingMapOfFetchedStock
   * It then updates all the items associated with that key, because all those items share the same set of attributes
   */
  const updateSingleRowPrice = (article: InventoryArticle, selectedPrice: number) => {
    const key = generateArticleKey(article)

    const copyOfWorkingMap = _.cloneDeep(workingFetchedStockMap)
    const entry = copyOfWorkingMap[key]
    if (entry) {
      entry.articles.forEach((a) => {
        a.price = selectedPrice
      })
    }
    setWorkingFetchedStockMap(copyOfWorkingMap)
  }

  /**
   *
   * when the user clicks on the radio button in the header, the min-max-avg price is applied to all items in the WorkingMapOfFetchedStock,
   * based on the corresponding min-max-avg price
   */
  const updatePriceOfAllArticles = (priceType: 'min' | 'avg' | 'max') => {
    const copyOfWorkingMap = _.cloneDeep(workingFetchedStockMap)

    Object.keys(copyOfWorkingMap).forEach((key) => {
      const entry = copyOfWorkingMap[key]
      const selectedPrice =
        priceType === 'min'
          ? getMin(entry.prices)
          : priceType === 'avg'
          ? getAverage(entry.prices)
          : getMax(entry.prices)
      entry.articles.forEach((article) => (article.price = selectedPrice))
    })

    setWorkingFetchedStockMap(copyOfWorkingMap)
  }

  /**
   * Once the user has applied the prices they want, the WorkingMapOfFetchedStock is converted to a list
   * and returned to OnboardingPresenter to publish the changes
   */
  const confirmUpdates = () => {
    const updatedInventory = Object.values(workingFetchedStockMap)
      .map((entry) => entry.articles)
      .reduce((acc, articles) => acc.concat(articles), [])

    setUpdatedStockAfterSettingsPricesAndComments?.(updatedInventory)
    onConfirm(updatedInventory)
  }

  return (
    <>
      <div className="w-100 d-flex justify-content-end mb-4">
        <Button variant="contained" onClick={confirmUpdates} data-testid="confirm-prices">
          {confirmLabel ?? t('common.confirm')}
        </Button>
      </div>
      <InventoryTable
        mapOfPricesForeachInventoryArticle={workingFetchedStockMap}
        getMin={getMin}
        getAverage={getAverage}
        getMax={getMax}
        updateSingleRowPrice={updateSingleRowPrice}
        updatePriceOfAllArticles={updatePriceOfAllArticles}
      />
    </>
  )
}

interface InventoryTableProps {
  mapOfPricesForeachInventoryArticle: InventoryPricesMap
  getMin: (prices: number[]) => number
  getAverage: (prices: number[]) => number
  getMax: (prices: number[]) => number
  updateSingleRowPrice: (article: InventoryArticle, selectedPrice: number) => void
  updatePriceOfAllArticles: (priceType: 'min' | 'avg' | 'max') => void
}

const InventoryTable: React.FC<InventoryTableProps> = ({
  mapOfPricesForeachInventoryArticle,
  getMin,
  getAverage,
  getMax,
  updateSingleRowPrice,
  updatePriceOfAllArticles,
}) => {
  const { t } = useTranslation()
  /**
   * Since the Table component cannot read maps, our database is converted to a list of objects
   */
  const dataForTable = Object.keys(mapOfPricesForeachInventoryArticle)
    .filter((key) => {
      const { prices } = mapOfPricesForeachInventoryArticle[key]
      return prices.length > 1
    })
    .map((key) => {
      const { articles, prices } = mapOfPricesForeachInventoryArticle[key]
      return {
        article: articles[0], // Takes the first article as the key representative
        prices,
      }
    })

  /**
   * allows you to activate the checkbox based on the user's choice
   */
  const [selectedPriceType, setSelectedPriceType] = useState<{
    [key: string]: 'min' | 'avg' | 'max' | null
  }>({})

  const [headerSelectedPriceType, setHeaderSelectedPriceType] = useState<
    'min' | 'avg' | 'max' | null
  >(null)

  const handleHeaderRadioChange = (priceType: 'min' | 'avg' | 'max') => {
    setHeaderSelectedPriceType(priceType)
    const copyOfSelectedPriceType = _.cloneDeep(selectedPriceType)

    dataForTable.forEach(({ article }) => {
      copyOfSelectedPriceType[generateArticleKey(article)] = priceType
    })
    setSelectedPriceType(copyOfSelectedPriceType)

    updatePriceOfAllArticles(priceType)
  }

  /**
   * builds the component by choosing the preset average price by default
   */
  useEffect(() => {
    handleHeaderRadioChange('avg')
  }, [])

  const renderHeaderRadioButton = (priceType: 'min' | 'avg' | 'max', label: string) => (
    <FormControlLabel
      control={
        <Radio
          data-testid={'global-radio-button-' + priceType}
          checked={headerSelectedPriceType === priceType}
        />
      }
      onClick={() => handleHeaderRadioChange(priceType)}
      label={label}
    />
  )

  return (
    <div
      style={{
        height:
          dataForTable.length === 0 ? 'auto' : `${Math.min(400, dataForTable.length * 50 + 90)}px`,
        width: '100%',
      }}
    >
      {dataForTable.length === 0 ? (
        <p className="fs-6 text-center">{t('onboarding.uniformPrices')}</p>
      ) : (
        <>
          <p>
            {dataForTable.length} {t('onboarding.rowsToUpdate')}
          </p>
          <AutoSizer>
            {({ height, width }) => (
              <Table
                width={width}
                height={height}
                headerHeight={40}
                rowHeight={50}
                rowCount={dataForTable.length}
                rowGetter={({ index }) => dataForTable[index]}
                className="onboarding-inventory-table"
              >
                <Column
                  width={58}
                  label=""
                  dataKey=""
                  disableSort={true}
                  cellRenderer={({ rowData }) => (
                    <Tooltip title={getGameFromId(rowData.article.card.idGame) || ''}>
                      <span
                        className={`ss-${getGameCodeFromName(
                          getGameFromId(rowData.article.card.idGame)
                        )} ss-game-icon ss-fw`}
                        style={{ fontSize: '1.5rem' }}
                      ></span>
                    </Tooltip>
                  )}
                />
                <Column
                  label=""
                  dataKey=""
                  width={60}
                  cellRenderer={({ rowData }) => (
                    <ImagePopover
                      variant="iconPopover"
                      imageComponent={
                        <img
                          width="62px"
                          src={getProductImageUrl(rowData.article.card._id.toString())}
                        />
                      }
                      imageId={rowData.article.card._id.toString()}
                    />
                  )}
                />
                <Column
                  label="Name"
                  dataKey="article.card.name"
                  width={200}
                  cellRenderer={({ rowData }) => <span>{rowData.article.card.name}</span>}
                />
                <Column
                  label="Cn"
                  dataKey="article.card.name"
                  width={60}
                  cellRenderer={({ rowData }) => <span>{rowData.article.card.cn}</span>}
                />
                <Column
                  label="Expansion"
                  dataKey="article.card.expansion"
                  width={200}
                  cellRenderer={({ rowData }) => <span>{rowData.article.card.expansion}</span>}
                />
                <Column
                  label="Attributes"
                  dataKey="article.attributes"
                  width={200}
                  cellRenderer={({ rowData }) => (
                    <div>
                      <ArticleAttributes article={rowData.article} iconHeight={17} />
                    </div>
                  )}
                />
                <Column
                  label="Min Price"
                  dataKey="minPrice"
                  headerRenderer={() => renderHeaderRadioButton('min', 'Min price')}
                  width={200}
                  cellRenderer={({ rowData, rowIndex }) => (
                    <FormControlLabel
                      control={
                        <Radio
                          data-testid={'radio-button-' + rowIndex}
                          checked={selectedPriceType[generateArticleKey(rowData.article)] === 'min'}
                        />
                      }
                      onClick={() => {
                        const key = generateArticleKey(rowData.article)
                        setSelectedPriceType((prev) => ({ ...prev, [key]: 'min' }))
                        updateSingleRowPrice(rowData.article, getMin(rowData.prices))
                      }}
                      label={getMin(rowData.prices).toString()}
                    />
                  )}
                />
                <Column
                  label="Avg Price"
                  dataKey="avgPrice"
                  headerRenderer={() => renderHeaderRadioButton('avg', 'Avg price')}
                  width={200}
                  cellRenderer={({ rowData, rowIndex }) => (
                    <FormControlLabel
                      control={
                        <Radio
                          data-testid={'radio-button-' + rowIndex}
                          checked={selectedPriceType[generateArticleKey(rowData.article)] === 'avg'}
                        />
                      }
                      onClick={() => {
                        const key = generateArticleKey(rowData.article)
                        setSelectedPriceType((prev) => ({ ...prev, [key]: 'avg' }))
                        updateSingleRowPrice(rowData.article, getAverage(rowData.prices))
                      }}
                      label={getAverage(rowData.prices).toString()}
                    />
                  )}
                />
                <Column
                  label="Max Price"
                  dataKey="maxPrice"
                  headerRenderer={() => renderHeaderRadioButton('max', 'Max price')}
                  width={200}
                  cellRenderer={({ rowData, rowIndex }) => (
                    <FormControlLabel
                      control={
                        <Radio
                          data-testid={'radio-button-' + rowIndex}
                          checked={selectedPriceType[generateArticleKey(rowData.article)] === 'max'}
                        />
                      }
                      onClick={() => {
                        const key = generateArticleKey(rowData.article)
                        setSelectedPriceType((prev) => ({ ...prev, [key]: 'max' }))
                        updateSingleRowPrice(rowData.article, getMax(rowData.prices))
                      }}
                      label={getMax(rowData.prices).toString()}
                    />
                  )}
                />
                <Column
                  label="Select Price"
                  dataKey="prices"
                  width={170}
                  cellRenderer={({ rowData, rowIndex }) => (
                    <Select
                      data-testid={'select-price-' + rowIndex}
                      style={{ width: '100px' }}
                      onChange={(e) => {
                        const key = generateArticleKey(rowData.article)
                        setSelectedPriceType((prev) => ({ ...prev, [key]: null }))
                        updateSingleRowPrice(rowData.article, Number(e.target.value))
                      }}
                    >
                      {rowData.prices.map((price: number) => (
                        <MenuItem key={price} value={price}>
                          {price}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Table>
            )}
          </AutoSizer>
        </>
      )}
    </div>
  )
}
